import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  FormLabel,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { PasswordInput } from '@/components/ui/password-input'

enum Roles {
  MECHANICAL_ENGINEER = 'mechanical_engineer',
  MANUFACTURING_ENGINEER = 'manufacturing_engineer',
  OTHER = 'other',
}
const AVAILABLE_ROLES = [
  {
    value: Roles.MECHANICAL_ENGINEER,
    label: 'Mechanical Engineer',
  },
  {
    value: Roles.MANUFACTURING_ENGINEER,
    label: 'Manufacturing Engineer',
  },
  {
    value: Roles.OTHER,
    label: 'Other',
  },
]
const formSchema = z
  .object({
    firstName: z
      .string()
      .min(1, 'First name  must contain at least 1 character(s)')
      .max(250, 'First name must be less than 250 character(s)'),
    lastName: z
      .string()
      .min(1, 'Last name must contain at least 1 character(s)')
      .max(250, 'Last name must be less than 250 character(s)'),
    role: z
      .string()
      .refine((val) => val !== '', { message: 'Role is required' }),
    email: z
      .string()
      .email()
      .max(250, 'Email must be less than 250 character(s)'),
    password: z
      .string()
      .min(8, 'Password must contain at least 8 character(s)')
      .max(250, 'Password must be less than 250 character(s)'),
    passwordConfirmation: z
      .string()
      .min(8, 'Password must contain at least 8 character(s)')
      .max(250, 'Password must be less than 250 character(s)'),
  })
  .refine((values) => values.password === values.passwordConfirmation, {
    message: 'Passwords must match',
    path: ['passwordConfirmation'],
  })

interface SignUpFormFieldsProps {
  isSubbmitting: boolean
  onSubmit: (values: z.infer<typeof formSchema>) => void
}

export const SignUpFormFields = ({
  isSubbmitting,
  onSubmit,
}: SignUpFormFieldsProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      role: '',
      email: '',
      password: '',
      passwordConfirmation: '',
    },
  })

  return (
    <Form {...form}>
      <form
        id="signup-form"
        name="signup-form"
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-8"
      >
        <div className="flex flex-col space-y-4">
          <div className="flex items-center space-x-2 w-full">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="w-1/2">
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input
                      className="placeholder:text-slate-400"
                      data-testid="signup-first-name-field"
                      placeholder="Enter your first name"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="w-1/2">
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input
                      className="placeholder:text-slate-400"
                      data-testid="signup-last-name-field"
                      placeholder="Enter your last name"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Role</FormLabel>
                <FormControl>
                  <Select
                    defaultValue={form.getValues('role')}
                    onValueChange={(selectedRole: Roles) => {
                      form.setValue('role', selectedRole)
                    }}
                    data-testid="signup-role-field"
                    {...field}
                  >
                    <SelectTrigger className="w-full rounded-lg px-5 focus:ring-0">
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent>
                      {AVAILABLE_ROLES.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    className="placeholder:text-slate-400"
                    placeholder="Enter your email"
                    data-testid="signup-email-field"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder="Enter your password"
                    testId="signup-password-field"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="passwordConfirmation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm Password</FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder="Confirm your password"
                    testId="signup-password-confirmation-field"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="w-full">
          <Button
            type="submit"
            disabled={isSubbmitting}
            data-testid="sign-up-button"
            className="w-full"
          >
            {isSubbmitting ? <Spinner /> : 'Sign Up'}
          </Button>
        </div>
      </form>
    </Form>
  )
}
